import { collection, query, where, documentId } from "firebase/firestore";
import { useCollection } from "react-firebase-hooks/firestore";

import { db } from "./firebase";

// Hunt member cap. Enforced on the join page (Join.jsx) and in firestore.rules.
// useHuntPlayers does not silently truncate: an over-cap memberIds (only
// reachable via admin SDK writes) surfaces as an error rather than a silently
// incomplete view.
export const MAX_MEMBERS = 32;

// Firestore's `in` query only accepts up to 30 values, so once MAX_MEMBERS
// exceeded 30 we have to split memberIds across multiple queries.
const IN_QUERY_LIMIT = 30;

// The display name to show for a player document. Falls back to the doc id,
// which keeps any orphaned name-keyed docs (e.g. left over from a partial
// migration) rendering as a name rather than a uid.
export function nameOf(playerDoc) {
    const d = playerDoc.data();
    return (d && (d.displayName || d.name)) || playerDoc.id;
}

// Loads the player documents for a hunt's members.
//
// `memberIds` semantics, so callers can pass it straight through while the
// hunt document is still loading:
//   - undefined / null / [] -> subscribe to nothing (returns no docs)
//   - [uid,...]             -> fetch just those members
//
// Returns a [snapshot-like, loading, error] tuple compatible with the
// useCollection shape consumers already use (snapshot.docs, snapshot.empty).
export function useHuntPlayers(memberIds) {
    const playersRef = collection(db, "players");
    const ids = Array.isArray(memberIds) ? memberIds : [];

    // Two fixed slots so the number of hooks called per render stays constant.
    // MAX_MEMBERS = 32 fits in two `in` queries (30 + up to 2).
    const chunk1 = ids.slice(0, IN_QUERY_LIMIT);
    const chunk2 = ids.slice(IN_QUERY_LIMIT, MAX_MEMBERS);

    const q1 = chunk1.length > 0
        ? query(playersRef, where(documentId(), "in", chunk1))
        : null;
    const q2 = chunk2.length > 0
        ? query(playersRef, where(documentId(), "in", chunk2))
        : null;

    const [snap1, loading1, error1] = useCollection(q1);
    const [snap2, loading2, error2] = useCollection(q2);

    if (ids.length > MAX_MEMBERS) {
        return [undefined, false, new Error(
            `memberIds has ${ids.length} entries but the cap is ${MAX_MEMBERS}`
        )];
    }

    const error = error1 || error2;
    if (error) {
        return [undefined, false, error];
    }

    // Still loading if a real query is in flight OR has been built but hasn't
    // produced a snapshot yet. The second clause covers the stale render right
    // after the parent supplies memberIds: useCollection's query reference has
    // changed but its subscribe effect hasn't run, so loading is briefly false
    // while snap is still undefined.
    const loading = loading1 || loading2
        || (q1 !== null && !snap1)
        || (q2 !== null && !snap2);
    if (loading) {
        return [undefined, true, undefined];
    }

    const docs = [...(snap1?.docs ?? []), ...(snap2?.docs ?? [])];
    return [{ docs, empty: docs.length === 0, size: docs.length }, false, undefined];
}
